/* ============================================================================
 * Ultimate Living — Yotpo reviews, brand-rendered.
 * ----------------------------------------------------------------------------
 * Pulls live review data from Yotpo's public storefront API and renders it
 * with OUR HTML / OUR class names. We don't load Yotpo's widget.js, so their
 * CSS class renames can't break our brand styling.
 *
 * Mount points:
 *   <span data-yotpo-bottomline data-product-id="123"></span>
 *   <div  data-yotpo-reviews    data-product-id="123" data-per-page="5"></div>
 *
 * Endpoints (public, no auth):
 *   GET  api-cdn.yotpo.com/products/{key}/{id}/bottomline
 *   GET  api-cdn.yotpo.com/v1/widget/{key}/products/{id}/reviews.json?per_page=N&page=M
 * ========================================================================== */

(function () {
  'use strict';

  // --- CONFIG -------------------------------------------------------------
  const CONFIG = {
    appKey: 'U53aQd9DY7MlQsYqAIw5F3mVZj34SCQaJ5g9BJ0W',
    base: 'https://api-cdn.yotpo.com',
    // Hosted Yotpo writer; we deep-link rather than build our own form.
    writerBase: 'https://shop.yotpo.com/widget/reviews/write',
  };

  // --- UTILS -------------------------------------------------------------
  // No `html` key: only textContent / setAttribute. Yotpo review content is
  // merchant + customer-controlled and could carry script payloads; never
  // route it through innerHTML.
  function el(tag, attrs, ...children) {
    const node = document.createElement(tag);
    // attrs may be passed as null (e.g. el('strong', null, text)); the default
    // param only covers undefined, so guard explicitly before Object.entries.
    for (const [k, v] of Object.entries(attrs || {})) {
      if (v == null) continue;
      if (k === 'class') node.className = v;
      else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v);
      else node.setAttribute(k, v);
    }
    for (const c of children) {
      if (c == null || c === false) continue;
      node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    }
    return node;
  }

  function clear(node) { while (node.firstChild) node.removeChild(node.firstChild); }

  function starGlyphs(score) {
    // Render 5 stars filled to nearest half. score is 0–5.
    const wrap = el('span', { class: 'yp-stars', 'aria-label': `${Math.round(score * 10) / 10} out of 5` });
    const filled = Math.round(score * 2) / 2;
    for (let i = 1; i <= 5; i++) {
      let cls = 'yp-star';
      if (i <= filled) cls += ' yp-star--full';
      else if (i - 0.5 === filled) cls += ' yp-star--half';
      wrap.appendChild(el('span', { class: cls, 'aria-hidden': 'true' }, '★'));
    }
    return wrap;
  }

  function fmtDate(iso) {
    try {
      return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(new Date(iso));
    } catch { return iso; }
  }

  async function getJson(url) {
    const res = await fetch(url, { credentials: 'omit', mode: 'cors' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  // --- REVIEW FILTER -----------------------------------------------------
  // FTC consumer-review rule (16 CFR Part 465): we do NOT suppress reviews by
  // star rating or sentiment. Negative reviews are displayed. Spam and fake
  // reviews are moderated upstream in Yotpo, the lawful place for it. Do not
  // add a rating or sentiment cut here; that would be review suppression.
  //
  // SEPARATE, MANDATORY filter (FDA / FTC dietary-supplement law): a supplement
  // may not be marketed as treating, curing, or preventing a disease. A customer
  // review claiming Green Miracle cured cancer (or any disease/cure claim) turns
  // the page into an illegal drug claim and invites FDA warning letters and
  // lawsuits, so it is not displayed. This is a CLAIM filter, not a rating
  // filter: it drops a review only when it makes a disease/cure claim, no matter
  // how positive. The durable fix is to also reject these in Yotpo moderation;
  // this render-time guard is the immediate defensive net. Extend the list below
  // (e.g. add other diseases) as counsel requires.
  const DISEASE_CLAIM_RE = /\b(cancers?|cancerous|carcinoma|tumou?rs?|oncolog(?:y|ist)|chemo(?:therapy)?|remission|cured?|cures|curing)\b/i;

  function makesDiseaseClaim(r) {
    if (!r) return false;
    return DISEASE_CLAIM_RE.test((r.content || '') + ' ' + (r.title || ''));
  }

  // Fulfillment/delivery complaints ("I never received this product") that were
  // nonetheless left at a HIGH star rating. They are not product feedback, so a
  // 5-star "never received it" card misrepresents the product. We drop them ONLY
  // when score >= 4, so genuine low-rated PRODUCT criticism is never suppressed
  // (suppressing that would violate FTC 16 CFR Part 465). The review still counts
  // toward the aggregate score/count; we just don't render the misleading card.
  // The durable fix is to correct or hide these in Yotpo moderation.
  var FULFILLMENT_RE = /\b(never (?:received|got|arrived|came|delivered)|have ?n.?t received|has ?n.?t (?:arrived|come|shipped)|did ?n.?t (?:receive|arrive|get)|not (?:yet )?(?:received|arrived|delivered)|still (?:waiting|have ?n.?t)|where.{0,3}s my order|never showed up|wrong (?:item|product)|empty (?:box|container))\b/i;

  function isFulfillmentComplaint(r) {
    if (!r || Number(r.score) < 4) return false;
    return FULFILLMENT_RE.test((r.content || '') + ' ' + (r.title || ''));
  }

  function isShowable(r) {
    if (!r) return false;
    if (makesDiseaseClaim(r)) return false;
    if (isFulfillmentComplaint(r)) return false;
    return true;
  }

  // Recency cutoff: only display review cards from this date onward. The
  // aggregate star score and total count (bottomline) still reflect ALL reviews;
  // this only limits which individual cards are shown. Yotpo returns reviews
  // newest-first, so once we hit one older than the cutoff we stop paginating.
  var MIN_REVIEW_DATE = '2025-01-01';
  function isRecent(r) {
    if (!r) return false;
    var d = r.created_at || r.date || r.created || '';
    if (!d) return true; // no date: don't hide it
    return String(d).slice(0, 10) >= MIN_REVIEW_DATE;
  }

  // --- BOTTOMLINE (product cards) ---------------------------------------
  async function loadBottomline(productId) {
    const url = `${CONFIG.base}/products/${CONFIG.appKey}/${productId}/bottomline`;
    const data = await getJson(url);
    return data?.response?.bottomline || { average_score: 0, total_reviews: 0 };
  }

  function renderBottomline(node, productId) {
    // skeleton: keep node's reserved height stable
    node.classList.add('yp-bottomline');
    node.appendChild(el('span', { class: 'yp-bottomline__skeleton', 'aria-hidden': 'true' }));

    loadBottomline(productId).then((b) => {
      clear(node);
      if (!b.total_reviews) return; // empty bottomline = render nothing, don't take space
      var score = Math.round((b.average_score || 0) * 10) / 10;
      node.appendChild(starGlyphs(b.average_score || 0));
      // Optional opt-in: when [data-show-score] is on the mount, render the
      // numeric score before the count (e.g. "4.9 · 213 verified reviews").
      if (node.hasAttribute('data-show-score')) {
        node.appendChild(el('strong', { class: 'yp-bottomline__score' }, String(score)));
        node.appendChild(el('span', { class: 'yp-bottomline__sep', 'aria-hidden': 'true' }, '·'));
      }
      var noun = b.total_reviews === 1 ? 'verified review' : 'verified reviews';
      node.appendChild(el('span', { class: 'yp-bottomline__count' }, `${b.total_reviews} ${noun}`));
    }).catch((err) => {
      clear(node);
      console.warn('[yp] bottomline failed', productId, err.message);
    });
  }

  // --- MAIN WIDGET (PDP) -------------------------------------------------
  async function loadReviews(productId, page, perPage) {
    const url = `${CONFIG.base}/v1/widget/${CONFIG.appKey}/products/${productId}/reviews.json?per_page=${perPage}&page=${page}`;
    const data = await getJson(url);
    return data?.response || null;
  }

  function renderDistribution(dist, total) {
    const wrap = el('div', { class: 'yp-distribution', 'aria-label': 'Star distribution' });
    for (let star = 5; star >= 1; star--) {
      const count = (dist && dist[star]) || 0;
      const pct = total > 0 ? Math.round((count / total) * 100) : 0;
      wrap.appendChild(el('div', { class: 'yp-distribution__row' },
        el('span', { class: 'yp-distribution__label' }, `${star} ★`),
        el('span', { class: 'yp-distribution__track' },
          el('span', { class: 'yp-distribution__fill', style: `width: ${pct}%;` })
        ),
        el('span', { class: 'yp-distribution__count' }, String(count))
      ));
    }
    return wrap;
  }

  function renderReviewCard(r) {
    const card = el('article', { class: 'yp-review' });

    // Stars (top, small, sage)
    card.appendChild(starGlyphs(r.score || 0));

    // Body (italic serif, decorative quotes via CSS pseudo-elements).
    // Title intentionally omitted — Yotpo titles tend to be the first sentence
    // of the body, which would render twice.
    if (r.content) card.appendChild(el('p', { class: 'yp-review__body' }, r.content));

    // Footer: author + verified tag, no date
    const author = r?.user?.display_name || 'Anonymous';
    const foot = el('p', { class: 'yp-review__foot' },
      el('span', { class: 'yp-review__author' }, author)
    );
    if (r.verified_buyer) {
      foot.appendChild(el('span', { class: 'yp-review__verified' }, 'Verified'));
    }
    card.appendChild(foot);

    return card;
  }

  function fmtCount(n) {
    try { return new Intl.NumberFormat('en-US').format(n); }
    catch { return String(n); }
  }

  function renderSkeleton(node) {
    clear(node);
    node.classList.add('yp-reviews');
    const skel = el('div', { class: 'yp-reviews__skeleton', 'aria-hidden': 'true' });
    skel.appendChild(el('div', { class: 'yp-skeleton-headline' }));
    const grid = el('div', { class: 'yp-skeleton-grid' });
    for (let i = 0; i < 3; i++) grid.appendChild(el('div', { class: 'yp-skeleton-card' }));
    skel.appendChild(grid);
    node.appendChild(skel);
  }

  function renderError(node) {
    clear(node);
    node.appendChild(el('div', { class: 'yp-reviews__error', role: 'alert' },
      el('p', null, 'Reviews are temporarily unavailable. Please refresh in a moment.')
    ));
  }

  function renderEmpty(node, productId) {
    clear(node);
    node.appendChild(el('div', { class: 'yp-reviews__empty' },
      el('p', { class: 'yp-reviews__empty-title' }, 'Be the first to write a review.'),
      el('a', {
        class: 'btn',
        href: writerLink(productId),
        target: '_blank', rel: 'noopener'
      }, 'Write a review')
    ));
  }

  function writerLink(productId) {
    return `${CONFIG.writerBase}?app_key=${CONFIG.appKey}&product_id=${productId}`;
  }

  function renderMain(node) {
    const productId = node.dataset.productId;
    const perPage = parseInt(node.dataset.perPage || '3', 10);
    const isCompact = node.hasAttribute('data-compact');
    if (!productId) return;

    renderSkeleton(node);

    // Editorial showcase: dynamic headline + 3-up grid + "READ ALL" inline expansion.
    // Internal state: how many pages of reviews we've fetched + appended so far.
    let loadedPages = 0;
    const accumulated = [];
    let totalReviews = 0;
    let avgScore = 0;
    let grid = null;
    let cta = null;
    let exhaustedRecent = false;

    function buildShell(score, total) {
      clear(node);
      node.classList.add('yp-reviews');
      if (isCompact) node.classList.add('yp-reviews--compact');

      const head = el('header', { class: 'yp-reviews__head' });
      if (node.dataset.eyebrow) {
        head.appendChild(el('p', { class: 'eyebrow' }, node.dataset.eyebrow));
      }
      if (isCompact) {
        // Compact: small headline with score + count on one line.
        head.appendChild(el('p', { class: 'yp-reviews__headline yp-reviews__headline--compact' },
          starGlyphs(score),
          el('strong', null, score.toFixed(1)),
          el('span', { class: 'yp-reviews__count' }, `${fmtCount(total)} verified buyers`)
        ));
      } else {
        head.appendChild(el('h2', { class: 'yp-reviews__headline' },
          `${score.toFixed(1)} stars across `,
          el('span', { class: 'yp-reviews__headline-italic' },
            `${fmtCount(total)} verified buyers.`)
        ));
      }
      node.appendChild(head);

      grid = el('div', { class: 'yp-reviews__grid' });
      node.appendChild(grid);

      // CTA — compact mode skips it
      if (!isCompact) {
        cta = el('div', { class: 'yp-readall' },
          el('button', {
            type: 'button',
            class: 'yp-readall__btn',
            onclick: loadNextPage
          }, `Read all ${fmtCount(total)} reviews →`)
        );
        node.appendChild(cta);
      }
    }

    function appendReviews(reviews) {
      reviews.forEach((r) => grid.appendChild(renderReviewCard(r)));
    }

    // Compact (cart) widget has no empty state — if every review is filtered
    // out, hide the whole social-proof block so the cart never shows a gap.
    function hideCompact() {
      const holder = (node.closest && node.closest('.cart-social-proof')) || node;
      holder.style.display = 'none';
    }

    function updateCta() {
      if (!cta) return;
      const shown = accumulated.length;
      // exhaustedRecent = reached the date cutoff (older reviews hidden) or the
      // last page. Recent reviews are few, so no numeric "X more" count.
      if (exhaustedRecent || shown >= totalReviews) {
        clear(cta);
        cta.appendChild(el('a', {
          class: 'yp-readall__btn',
          href: writerLink(productId),
          target: '_blank', rel: 'noopener'
        }, 'Write a review →'));
      } else {
        clear(cta);
        cta.appendChild(el('button', {
          type: 'button',
          class: 'yp-readall__btn',
          onclick: loadNextPage
        }, 'Read more reviews →'));
      }
    }

    function loadNextPage() {
      const nextPage = loadedPages + 1;
      // Disable while loading
      if (cta) cta.setAttribute('aria-busy', 'true');
      loadReviews(productId, nextPage, perPage).then((res) => {
        if (!res) return;
        const raw = res.reviews || [];
        if (raw.length < perPage || raw.some(function (r) { return !isRecent(r); })) exhaustedRecent = true;
        const reviews = raw.filter(function (r) { return isShowable(r) && isRecent(r); });
        accumulated.push(...reviews);
        loadedPages = nextPage;
        appendReviews(reviews);
        if (cta) cta.removeAttribute('aria-busy');
        updateCta();
      }).catch((err) => {
        console.warn('[yp] load page failed', productId, nextPage, err.message);
        if (cta) cta.removeAttribute('aria-busy');
      });
    }

    // First load. Compact (cart) only needs a couple of positive reviews, so we
    // over-fetch and filter, guaranteeing the grid can be filled with showable
    // ones even when the first few are delivery complaints.
    const firstFetch = isCompact ? Math.max(perPage * 8, 16) : perPage;
    loadReviews(productId, 1, firstFetch).then((res) => {
      if (!res) { if (isCompact) return hideCompact(); return renderError(node); }
      const bottomline = res.bottomline || { average_score: 0, total_review: 0 };
      const allReviews = res.reviews || [];
      if (allReviews.length < firstFetch || allReviews.some(function (r) { return !isRecent(r); })) exhaustedRecent = true;
      const showable = allReviews.filter(function (r) { return isShowable(r) && isRecent(r); });
      totalReviews = res.pagination?.total ?? bottomline.total_review ?? allReviews.length;
      avgScore = bottomline.average_score || 0;

      if (isCompact) {
        const display = showable.slice(0, perPage);
        if (display.length === 0) return hideCompact();
        buildShell(avgScore, totalReviews);
        accumulated.push(...display);
        loadedPages = 1;
        appendReviews(display);
        return;
      }

      if (totalReviews === 0) return renderEmpty(node, productId);

      buildShell(avgScore, totalReviews);
      accumulated.push(...showable);
      loadedPages = 1;
      appendReviews(showable);
      updateCta();
    }).catch((err) => {
      console.warn('[yp] main widget failed', productId, err.message);
      renderError(node);
    });
  }

  // --- BOOT --------------------------------------------------------------
  // Track which nodes we've already rendered so we don't double-fetch.
  const rendered = new WeakSet();

  function scanAndRender() {
    document.querySelectorAll('[data-yotpo-bottomline]').forEach((node) => {
      if (rendered.has(node)) return;
      const id = node.dataset.productId;
      if (id) { rendered.add(node); renderBottomline(node, id); }
    });
    document.querySelectorAll('[data-yotpo-reviews]').forEach((node) => {
      if (rendered.has(node)) return;
      rendered.add(node);
      renderMain(node, 1);
    });
  }

  function init() {
    scanAndRender();
    // If other scripts inject yotpo mount points later (Recharge re-renders,
    // SPA navigation, etc.) catch them via MutationObserver. Cheap idempotent.
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(scanAndRender);
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
